import React from 'react';
import Form from 'react-bootstrap/Form';
import MASpinner from '../../common/MASpinner';
import { change, Field, formValueSelector, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { consultantBroadcastCreate, uploadTicketAttachment } from '../../../lib/redux/actions/consultantTickets';
import { renderCheckbox, renderField, renderReactSelect, renderSelect } from '../../../lib/helper/form';
import { addToast } from '../../../lib/redux/actions/toast';
import { consultantTypeListFetch } from '../../../lib/redux/actions/consultantType';
import { consultantSpecialityListFetch } from '../../../lib/redux/actions/consultantSpeciality';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';
import FileUploader from '../../file/FileUploader';

const FORM_NAME = 'CreateConsultantBroadcastForm';
const selector = formValueSelector(FORM_NAME);

const mapStateToProps = (state) => {
  return {
    ...state.consultantTypes,
    ...state.consultantTicketTypes,
    ...state.consultantSpecialities,
    consultantSpecialityValue: selector(state, 'consultantSpeciality'),
    consultantTypeValue: selector(state, 'consultantType'),
    includeSecretariesValue: selector(state, 'includeSecretaries'),
    feeAssuredInsurerValue: selector(state, 'feeAssuredInsurer'),
    hospitalValue: selector(state, 'hospital'),
  };
};
const mapDispatchToProps = {
  consultantTypeListFetch,
  consultantSpecialityListFetch,
  consultantBroadcastCreate,
  uploadTicketAttachment,
  addToast,
  changeField: (field, value) => change(FORM_NAME, field, value),
};

class CreateConsultantBroadcastForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      recipients: [],
      recipientsExpanded: true,
      excludedRecipients: new Set(),
      healthInsurers: [],
      hospitals: [],
      pendingAttachments: [],
    };
  }

  componentDidMount() {
    const { consultantTypeListFetch, consultantSpecialityListFetch } = this.props;
    consultantTypeListFetch();
    consultantSpecialityListFetch();
    requests.get(API_ROUTES.HEALTH_INSURER, { pagination: false, isAnalogue: false }).then((response) => {
      this.setState({ healthInsurers: response['hydra:member'] || [] });
    });
    requests.get(API_ROUTES.HOSPITALS, { pagination: false }).then((response) => {
      this.setState({ hospitals: response['hydra:member'] || [] });
    });
  }

  onSubmit(values) {
    const { consultantBroadcastCreate, uploadTicketAttachment, reset, addToast } = this.props;
    const { pendingAttachments } = this.state;

    return consultantBroadcastCreate({
      message: values.message,
      consultantType: values.consultantType,
      includeSecretaries: values.includeSecretaries ?? false,
      consultantSpecialities: values.consultantSpeciality ?? [],
      feeAssuredInsurer: values.feeAssuredInsurer || null,
      hospital: values.hospital || null,
      excludedRecipients: [...this.state.excludedRecipients],
    }).then((response) => {
      const ticketId = response && response.id;
      if (ticketId && pendingAttachments.length > 0) {
        return Promise.all(pendingAttachments.map((file) => uploadTicketAttachment(ticketId, file))).then(() => {
          addToast('The Broadcast was created', true);
          this.setState({ recipients: [], excludedRecipients: new Set(), pendingAttachments: [] });
          reset();
        });
      }
      addToast('The Broadcast was created', true);
      this.setState({ recipients: [], excludedRecipients: new Set(), pendingAttachments: [] });
      reset();
    });
  }

  handleFilesAdded(e) {
    const files = Array.from(e.target.files || []);
    this.setState((prev) => ({
      pendingAttachments: [...prev.pendingAttachments, ...files],
    }));
  }

  removeAttachment(index) {
    this.setState((prev) => ({
      pendingAttachments: prev.pendingAttachments.filter((_, i) => i !== index),
    }));
  }

  getListOfRecipients(type, specialities, feeAssuredInsurer, hospital) {
    let params = {
      consultantType: type,
      consultantSpecialities: specialities,
    };
    if (feeAssuredInsurer) params.feeAssuredInsurer = feeAssuredInsurer;
    if (hospital) params.hospital = hospital;
    requests.get(API_ROUTES.BROADCAST_RECIPIENTS, params).then((response) => {
      this.setState({
        recipients: response,
        excludedRecipients: new Set(),
      });
    });
  }

  toggleRecipient(email) {
    this.setState((prevState) => {
      const excluded = new Set(prevState.excludedRecipients);
      if (excluded.has(email)) {
        excluded.delete(email);
      } else {
        excluded.add(email);
      }
      return { excludedRecipients: excluded };
    });
  }

  toggleAllRecipients() {
    this.setState((prevState) => {
      if (prevState.excludedRecipients.size === 0) {
        return { excludedRecipients: new Set(prevState.recipients.map((r) => r.email)) };
      }
      return { excludedRecipients: new Set() };
    });
  }

  render() {
    const {
      handleSubmit,
      error,
      pristine,
      submitting,
      isCreatingConsultantTicket,
      isFetchingConsultantTypes,
      consultantTypes = [],
      consultantSpecialities = [],
      consultantTypeValue,
      consultantSpecialityValue,
      feeAssuredInsurerValue,
      hospitalValue,
      changeField,
    } = this.props;

    const { healthInsurers, hospitals, recipients, recipientsExpanded, excludedRecipients, pendingAttachments } = this.state;

    if (isFetchingConsultantTypes) {
      return <MASpinner />;
    }

    const typeEveryone = { text: 'All Consultants and Anaesthetists', value: 'all' };
    const typeOptions = consultantTypes.map((consultantType) => ({
      value: consultantType['@id'],
      text: `All ${consultantType.displayName}s`,
    }));
    typeOptions.push(typeEveryone);

    const specialitiesOptions = consultantSpecialities.map((s) => ({ value: s['@id'], label: s.name }));
    const selectedSpecialities = Array.isArray(consultantSpecialityValue)
      ? specialitiesOptions.filter((o) => consultantSpecialityValue.includes(o.value))
      : [];

    const insurerOptions = [...healthInsurers].sort((a, b) => a.name.localeCompare(b.name)).map((hi) => ({ value: hi['@id'], text: hi.name }));
    const hospitalOptions = [...hospitals].sort((a, b) => a.name.localeCompare(b.name)).map((h) => ({ value: h['@id'], text: h.name }));

    const selectedCount = recipients.length - excludedRecipients.size;
    const allSelected = excludedRecipients.size === 0;

    return (
        <>
      <div className="invoice-batch-single__section mb-3">
        <div className="invoice-batch-single__section-icon">
          <i className="fas fa-edit" />
        </div>

        <h2 className="invoice-batch-single__section-header">Create Broadcast</h2>
        <p>Broadcast a message to all active Clients (and optionally their secretaries)</p>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form onSubmit={handleSubmit(this.onSubmit.bind(this))}>
          <div className={'row mb-4'}>
            <div className={'col-sm-3'}>
              <Field
                name="consultantType"
                options={typeOptions}
                emptyLabel={'Please select'}
                label="Broadcast To"
                component={renderSelect}
                required={true}
                onChange={(e) =>
                  this.getListOfRecipients(e.target.value, consultantSpecialityValue, feeAssuredInsurerValue, hospitalValue)
                }
              />
              <Field wrapperClassNames="checkbox" name="includeSecretaries" label="Include Secretaries" id={`includeSecretaries`} component={renderCheckbox} />
            </div>
            <div className={'col-sm-3'}>
              <Field
                name="consultantSpeciality"
                label="And/Or Speciality"
                options={specialitiesOptions}
                selectedOptions={selectedSpecialities}
                handleSelect={(selected) => {
                  let specialities = selected ? selected.map((o) => o.value) : [];
                  changeField('consultantSpeciality', specialities);
                  this.getListOfRecipients(consultantTypeValue, specialities, feeAssuredInsurerValue, hospitalValue);
                }}
                isMulti
                component={renderReactSelect}
              />
            </div>
            <div className={'col-sm-3'}>
              <Field
                name="feeAssuredInsurer"
                options={insurerOptions}
                emptyLabel={'Any insurer'}
                label="Fee Assured With Insurer"
                component={renderSelect}
                onChange={(e) =>
                  this.getListOfRecipients(consultantTypeValue, consultantSpecialityValue, e.target.value || null, hospitalValue)
                }
              />
            </div>
            <div className={'col-sm-3'}>
              <Field
                name="hospital"
                options={hospitalOptions}
                emptyLabel={'Any hospital'}
                label="Works At Hospital"
                component={renderSelect}
                onChange={(e) =>
                  this.getListOfRecipients(consultantTypeValue, consultantSpecialityValue, feeAssuredInsurerValue, e.target.value || null)
                }
              />
            </div>
          </div>
          <div className={'row'}>
            <div className={'col-sm-12'}>
              <Field
                className="mb-2"
                style={{ minHeight: '200px' }}
                name="message"
                label="Message"
                id="message"
                type="textarea"
                required={true}
                component={renderField}
              />
              <div className="form-group">
                <label>Attachments (optional)</label>
                <FileUploader
                  title="Click to add attachments (PDF, Word, Excel, CSV, images — max 20 MB each)"
                  onFilesAdded={this.handleFilesAdded.bind(this)}
                  multiple={true}
                  sizeLimitMB={20}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp"
                  requestInProgress={isCreatingConsultantTicket}
                />
                {pendingAttachments.length > 0 && (
                  <ul className="list-unstyled mt-2">
                    {pendingAttachments.map((file, index) => (
                      <li key={index} className="d-flex align-items-center mb-1">
                        <i className="fas fa-paperclip mr-2 text-secondary" />
                        <span className="mr-2">{file.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={() => this.removeAttachment(index)}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {isCreatingConsultantTicket ? (
                <MASpinner />
              ) : (
                <button type="submit" disabled={pristine || submitting} className="btn btn-primary btn-block mb-2">
                  Create Broadcast
                </button>
              )}
            </div>
          </div>
        </Form>
      </div>
    {recipients.length > 0 && (
        <div className={'row mb-3'}>
          <div className={'col-sm-12'}>
            <div className="card no-shadow">
              <div
                  className="card-header d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setState((s) => ({ recipientsExpanded: !s.recipientsExpanded }))}>
                  <span>
                    Broadcast Recipients ({selectedCount} selected / {recipients.length} total)
                  </span>
                <i className={`fas fa-chevron-${recipientsExpanded ? 'up' : 'down'}`} />
              </div>
              {recipientsExpanded && (
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item py-1 bg-light">
                      <label className="mb-0" style={{ cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={this.toggleAllRecipients.bind(this)}
                            className="mr-2"
                        />
                        <strong>{allSelected ? 'Deselect all' : 'Select all'}</strong>
                      </label>
                    </li>
                    {recipients.map((r) => (
                        <li key={r.email} className="list-group-item py-1">
                          <label className="mb-0 w-100" style={{ cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={!excludedRecipients.has(r.email)}
                                onChange={() => this.toggleRecipient(r.email)}
                                className="mr-2"
                            />
                            {r.salutation ? `${r.salutation} ` : ''}{r.name}
                          </label>
                        </li>
                    ))}
                  </ul>
              )}
            </div>
          </div>
        </div>
    )}
    </>
    );
  }
}

// Decorate with reduxForm(). It will read the initialValues prop provided by connect()

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
  })(CreateConsultantBroadcastForm),
);
